import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import allocateManagementApi, {
  AllocateLogFilter,
  AllocateLogItem,
  AllocateLogRequestParams,
} from "@api/allocateManagementApi";
import productApi from "@api/productApi";
import { TRYALL_STOCK_CHANNEL_ID } from "@constant/AllocationChannel";
import type { RootState } from "../rootReducer";

interface IState {
  isFetching: boolean;
  list: {
    count: number;
    next: string | null;
    previous: string | null;
    results: AllocateLogItem[];
  };
  filter: AllocateLogFilter;
  skuOptions: string[];
}

const initialState: IState = {
  isFetching: false,
  list: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  filter: {
    sku: undefined,
    productName: undefined,
    sourceId: undefined,
    destId: undefined,
    createdAtAfter: undefined,
    createdAtBefore: undefined,
    staffId: undefined,
    limit: 20,
    offset: 0,
  },
  skuOptions: [],
};

const buildAllocateLogRequestParams = ({
  sku,
  sourceId,
  destId,
  ...rest
}: AllocateLogFilter): AllocateLogRequestParams => ({
  ...rest,
  sku: sku && sku.length > 0 ? sku.join(",") : undefined,
  sourceId: sourceId === TRYALL_STOCK_CHANNEL_ID ? "" : sourceId,
  destId: destId === TRYALL_STOCK_CHANNEL_ID ? "" : destId,
});

export const fetchAllocateLogList = createAsyncThunk(
  "allocateLog/fetchAllocateLogList",
  async (params: AllocateLogFilter) => {
    const response = await allocateManagementApi.fetchAllocateLogList(buildAllocateLogRequestParams(params));
    return response;
  },
);

export const fetchSkuOptions = createAsyncThunk("allocateLog/fetchSkuOptions", async (skuQ: string) => {
  const response = await productApi.fetchProductDetailListByFilter({ sku: skuQ });
  return response.results.map((r) => r.sku);
});

const allocateLogSlice = createSlice({
  name: "allocateLog",
  initialState,
  reducers: {
    setAllocateLogFilter: (state, action) => {
      state.filter = action.payload;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllocateLogList.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(fetchAllocateLogList.fulfilled, (state, action) => {
      state.isFetching = false;
      state.list = action.payload;
    });
    builder.addCase(fetchAllocateLogList.rejected, (state) => {
      state.isFetching = false;
    });
    builder.addCase(fetchSkuOptions.fulfilled, (state, action) => {
      state.skuOptions = action.payload;
    });
  },
});

export const allocateLogState = (state: RootState) => state.allocateLogSlice;
export const { setAllocateLogFilter, reset: resetAllocateLog } = allocateLogSlice.actions;
export default allocateLogSlice.reducer;
